import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'

import {
  DespatchAdvice,
  DespatchAdviceItem,
  WebEdiDto,
} from '../../../../../../data/models'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import { isItemInitial } from '../../../../../../utils/helper/checkConfirmationCodes'
import {
  ConfigurableComponent,
  ConfigurableTable,
  convertOrderItems,
} from '../../../../../../utils/wrapper'
import { DefaultConfig } from '../../../../../ui-config/data'

type Props = {
  config: DefaultConfig
  despatchAdvice?: WebEdiDto<DespatchAdvice>
  despatchAdviceItems?: DespatchAdviceItem[]
  isDespatchAdviceItemsLoading: boolean
  isRequestRunning: any
  isVisible: boolean
  setIsVisible: (params: boolean) => void
  currentDate: Date
  handleSubmit: () => void
  handleClose: () => void
  isDisabled: boolean
  tooltip: JSX.Element | null
}

export const AcceptView: React.FC<Props> = ({
  config,
  despatchAdvice,
  despatchAdviceItems,
  isDespatchAdviceItemsLoading,
  isRequestRunning,
  isVisible,
  setIsVisible,
  currentDate,
  handleSubmit,
  handleClose,
  isDisabled,
  tooltip,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  // const tooltip = isDisabled ? t('common.buttons.sendTooltip') : ''

  const renderLabel = (labelText: string) => (
    <label className="text-sm" htmlFor={labelText}></label>
  )

  const renderInputText = (id: string, value?: string) => (
    <InputText id={id} value={value} className="w-full" disabled />
  )

  const renderCalendar = (id: string, value: Date) => (
    <Calendar
      id={id}
      value={value}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  // deliveryNoteNumber
  const deliveryNoteNumberLabel = renderLabel('deliveryNoteNumber')
  const deliveryNoteNumberComponent = renderInputText(
    'deliveryNoteNumber',
    despatchAdvice?.Payload.DeliveryNoteNumber,
  )

  const deliveryNoteNumberWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.acceptDespatchAdvice}
      component={deliveryNoteNumberComponent}
      label={deliveryNoteNumberLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // deliveryNoteDate
  const deliveryNoteDateLabel = renderLabel('deliveryNoteDate')
  const deliveryNoteDateComponent = renderCalendar(
    'deliveryNoteDate',
    currentDate,
  )

  const deliveryNoteDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.acceptDespatchAdvice}
      component={deliveryNoteDateComponent}
      label={deliveryNoteDateLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // deliveryDate
  const deliveryDateLabel = renderLabel('deliveryDate')
  const deliveryDateComponent = renderCalendar(
    'deliveryDate',
    new Date(despatchAdvice?.Payload.DeliveryDate!),
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={config.despatchAdvice.acceptDespatchAdvice}
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // footer
  const footer = (
    <>
      <Button
        label={t('common.buttons.cancel')}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t('common.buttons.send')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={handleSubmit}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      {tooltip}
      <Button
        label={t('common.buttons.send')}
        icon="pi pi-check"
        className="send-button"
        onClick={() => setIsVisible(true)}
        disabled={isDisabled}
        visible={config.despatchAdvice.acceptDespatchAdvice.enabled}
      />

      <Dialog
        visible={isVisible}
        header={t('despatchAdvice.title.send.dialog.title')}
        style={{ width: '60rem' }}
        breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          {/* deliveryNoteNumber */}
          {deliveryNoteNumberWrapper}

          {/* deliveryDate */}
          {deliveryNoteDateWrapper}

          {/* deliveryDate */}
          {deliveryDateWrapper}
        </div>

        <h3>{t('despatchAdvice.title.send.dialog.table.title')}</h3>
        {isItemInitial(despatchAdviceItems!) && (
          <Message
            className="mb-3"
            text={t(
              'despatchAdvice.title.send.dialog.table.info.statusToAccept',
            )}
          />
        )}

        <ConfigurableTable
          config={config.despatchAdvice.acceptDespatchAdvice.table}
          data={convertOrderItems(despatchAdviceItems)}
          isLoading={isDespatchAdviceItemsLoading}
          stateKey=""
        />
      </Dialog>
    </>
  )
}
